import React, { forwardRef, useCallback, useMemo } from 'react'

import { DndContext, DragEndEvent, closestCorners } from '@dnd-kit/core'
import {
  restrictToHorizontalAxis,
  restrictToParentElement,
} from '@dnd-kit/modifiers'
import { SortableContext } from '@dnd-kit/sortable'

import { TableHeaders } from './TableHeaders'
import { TableColumnConfig, TableSorter, OnColumnReorder } from './types'
import {
  Table as TableElement,
  TableBody as TableBodyElement,
} from '@/Atomic/Templates/Table'

const modifiers = [restrictToHorizontalAxis, restrictToParentElement]

export interface TablePropsBase {
  /**
   * Contains table columns configuration
   */
  columns: TableColumnConfig[]
  /**
   * Sets data-testid attribute on table element
   */
  testId?: string
  /**
   * Extends table wrapper classNames
   */
  wrapperClassName?: string
  /**
   * Extends table classNames
   */
  className?: string
  /**
   * Sets content inside the table tbody element
   */
  children: React.ReactNode
  /**
   * Current sort configuration
   */
  sorter?: TableSorter
  /**
   * Called on sorting element click
   */
  sortColumn?: (accessor: string) => void
  /**
   * Specifies whether to hide table headers
   */
  hideHeaders?: boolean
  /**
   * Sets some inner Table components
   */
  components?: {
    RowsWrapper?: React.FunctionComponent
  }
  selectAllRows?: (select?: boolean) => void
  isAllRowsSelected?: () => boolean | undefined
  isAllRowsSelectDisabled?: () => boolean
}

export interface TablePropWithReorder extends TablePropsBase {
  /**
   * Defines column accessors order
   */
  accessors?: string[]
  /**
   * Callback for column reorder event
   */
  onColumnReorder: OnColumnReorder
}

export interface TablePropsWithoutReorder extends TablePropsBase {
  accessors?: never
  onColumnReorder?: never
}

export type TableProps = TablePropsWithoutReorder | TablePropWithReorder

export const Table = forwardRef<HTMLTableElement, TableProps>(
  (
    {
      className = '',
      wrapperClassName = '',
      testId,
      columns,
      sorter = null,
      sortColumn,
      accessors,
      onColumnReorder,
      hideHeaders,
      children,
      components = {},
      selectAllRows,
      isAllRowsSelected,
      isAllRowsSelectDisabled,
    },
    _ref,
  ) => {
    // TODO: PASS CONFIGS BY CONTEXT
    const { RowsWrapper = TbodyElement } = components
    const isReorderingColumnsActive = !!onColumnReorder

    const columnsOrder = useMemo(() => {
      if (accessors) {
        return accessors
      }

      return columns.map(({ accessor }) => accessor)
    }, [accessors, columns])

    const onColumnDragEnd = useCallback(
      (event: DragEndEvent) => {
        if (onColumnReorder) {
          const { active, over } = event

          if (over?.id && active.id !== over?.id) {
            const oldIndex = columnsOrder.indexOf(active.id + '')
            const newIndex = columnsOrder.indexOf(over.id + '')

            onColumnReorder(oldIndex, newIndex)
          }
        }
      },
      [columnsOrder, onColumnReorder],
    )

    return (
      <div className={`${wrapperClassName}`}>
        <TableElement
          // ref={ref}
          data-testid={testId}
          className={className}
        >
          <DndContext
            onDragEnd={onColumnDragEnd}
            collisionDetection={closestCorners}
            modifiers={modifiers}
            accessibility={{ container: document.body }}
          >
            <SortableContext items={columnsOrder}>
              {!hideHeaders && (
                <TableHeaders
                  isReorderingColumnsActive={isReorderingColumnsActive}
                  columns={columns}
                  sorter={sorter}
                  sortColumn={sortColumn}
                  selectAllRows={selectAllRows}
                  isAllRowsSelected={isAllRowsSelected}
                  isAllRowsSelectDisabled={isAllRowsSelectDisabled}
                />
              )}
            </SortableContext>
          </DndContext>

          <RowsWrapper>{children}</RowsWrapper>
        </TableElement>
      </div>
    )
  },
)

const TbodyElement: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <TableBodyElement>{children}</TableBodyElement>

Table.displayName = 'Table'

export default Table
